import Executive from '../../framework/rmi-util'
import { register } from '../../framework/rmi-server-util'
import { CheckPolicy, CheckPolicyOrApiKey, Resource } from '../../framework/permission-util'
import { RT } from '../permissions'
import { operatorsTechnical } from './OperatorsTechnical'
import type { AuditFeedRequest, AuditFeedResult, ContentHubRolesResult, CreateOperatorRequest, CreateOperatorResult, DeleteOperatorResult, OperatorListRequest, OperatorListResult, PermissionCeiling, ReportCenterDimensionsResult, TagSummary, UpdateOperatorPermissionsRequest, UpdateOperatorPermissionsResult } from './OperatorsTechnical'
import type { ProvisioningState, ProvisioningSystem } from './operators.entities'
import type { PeakRole } from './partners/partner-clients'

export default class OperatorsExecutive extends Executive {
	initServer() {}

	@CheckPolicy()
	async getOperators(@Resource(RT.CLIENT) clientId: string, request: OperatorListRequest): Promise<OperatorListResult> {
		return operatorsTechnical.getOperators(clientId, request ?? {})
	}

	@CheckPolicy()
	async createOperator(@Resource(RT.CLIENT) clientId: string, request: CreateOperatorRequest): Promise<CreateOperatorResult> {
		return operatorsTechnical.createOperator(clientId, request)
	}

	// tag CRUD is either-auth: operators via policy, Toolbox via the shared api key
	@CheckPolicyOrApiKey()
	async getTags(@Resource(RT.CLIENT) clientId: string, options?: { withUsage?: boolean }): Promise<TagSummary[]> {
		return operatorsTechnical.getTags(clientId, options)
	}

	@CheckPolicyOrApiKey()
	async createTag(@Resource(RT.CLIENT) clientIds: string | string[], name: string): Promise<TagSummary[]> {
		return operatorsTechnical.createTag(clientIds, name)
	}

	@CheckPolicyOrApiKey()
	async deleteTag(@Resource(RT.CLIENT) clientId: string, tagId: string): Promise<{ deleted: boolean, usageCount: number }> {
		return operatorsTechnical.deleteTag(clientId, tagId)
	}

	// MYS-7148: Peak backoffice login — own account, or on behalf of another operator of
	// the client (request.userId; audited). See Technical for the checks.
	@CheckPolicy()
	async getPeaksolutionLoginUrl(@Resource(RT.CLIENT) clientId: string, request?: { userId?: string }): Promise<{ url: string }> {
		return operatorsTechnical.getPeaksolutionLoginUrl(clientId, request)
	}

	@CheckPolicy()
	async getPermissionCeiling(@Resource(RT.CLIENT) clientId: string): Promise<PermissionCeiling> {
		return operatorsTechnical.getPermissionCeiling(clientId)
	}

	@CheckPolicy()
	async updateOperatorPermissions(@Resource(RT.CLIENT) clientId: string, request: UpdateOperatorPermissionsRequest): Promise<UpdateOperatorPermissionsResult> {
		return operatorsTechnical.updateOperatorPermissions(clientId, request)
	}

	@CheckPolicy()
	async deleteOperator(@Resource(RT.CLIENT) clientId: string, request: { userId: string }): Promise<DeleteOperatorResult> {
		return operatorsTechnical.deleteOperator(clientId, request)
	}

	@CheckPolicy()
	async setTwoFactor(@Resource(RT.CLIENT) clientId: string, request: { userId: string, enabled: boolean }): Promise<void> {
		return operatorsTechnical.setTwoFactor(clientId, request)
	}

	@CheckPolicy()
	async setOperatorEnabled(@Resource(RT.CLIENT) clientId: string, request: { userId: string, enabled: boolean }): Promise<void> {
		return operatorsTechnical.setOperatorEnabled(clientId, request)
	}

	@CheckPolicy()
	async resendRecoveryEmail(@Resource(RT.CLIENT) clientId: string, request: { userId: string }): Promise<void> {
		return operatorsTechnical.resendRecoveryEmail(clientId, request)
	}

	@CheckPolicy()
	async getContentHubRoles(@Resource(RT.CLIENT) clientId: string): Promise<ContentHubRolesResult> {
		return operatorsTechnical.getContentHubRoles(clientId)
	}

	@CheckPolicy()
	async retryProvisioning(@Resource(RT.CLIENT) clientId: string, request: { userId: string, system: ProvisioningSystem }): Promise<ProvisioningState> {
		return operatorsTechnical.retryProvisioning(clientId, request)
	}

	@CheckPolicy()
	async getReportCenterDimensions(@Resource(RT.CLIENT) clientId: string): Promise<ReportCenterDimensionsResult> {
		return operatorsTechnical.getReportCenterDimensions(clientId)
	}

	@CheckPolicy()
	async getPeakRoles(@Resource(RT.CLIENT) clientId: string): Promise<PeakRole[]> {
		return operatorsTechnical.getPeakRoles(clientId)
	}

	// read-only client audit feed — dashboard only (no api-key door)
	@CheckPolicy()
	async getClientAuditLog(@Resource(RT.CLIENT) clientId: string, request?: AuditFeedRequest): Promise<AuditFeedResult> {
		return operatorsTechnical.getClientAuditLog(clientId, request)
	}
}

register(OperatorsExecutive)
